import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SkuCombobox from "@/components/SkuCombobox";
import StateDistrictSelect from "@/components/StateDistrictSelect";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Sparkles, ExternalLink, Search, MessageSquare } from "lucide-react";
import B2BChat from "@/components/B2BChat";

const emptyItem = () => ({ sku_id: "", quantity: 1 });
const emptyForm = () => ({
  party_name: "", party_gst: "",
  party_billing_address: "", party_shipping_address: "",
  party_phone: "",
  party_state: "", party_district: "", party_city: "",
  notes: "",
});

const STATUS_COLOR = {
  "Approved": "bg-green-100 text-green-800",
  "Pending Approval": "bg-amber-100 text-amber-800",
  "Rejected": "bg-red-100 text-red-700",
  "Awaiting Invoice": "bg-blue-100 text-blue-800",
};

export default function IBOBills() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || (user.roles || []).includes("admin");
  const [items, setItems] = useState([emptyItem()]);
  const [form, setForm] = useState(emptyForm());
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [chatOpen, setChatOpen] = useState(null); // holds the row when open

  const loadSkus = useCallback(async () => {
    try {
      const { data } = await api.get("/b2b/skus");
      setSkus(data);
    } catch { /* silent */ }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      const { data } = await api.get("/b2b/my-submissions");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadSkus(); loadRows(); }, [loadSkus, loadRows]);

  const setItem = (idx, key, val) => {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };
  const addItem = () => setItems((cur) => [...cur, emptyItem()]);
  const removeItem = (idx) => setItems((cur) => cur.length > 1 ? cur.filter((_, i) => i !== idx) : cur);

  const resetForm = () => { setForm(emptyForm()); setItems([emptyItem()]); };

  const submit = async () => {
    const validItems = items
      .filter((it) => it.sku_id && Number(it.quantity) > 0)
      .map((it) => ({ sku_id: it.sku_id, quantity: Number(it.quantity) }));
    if (!form.party_name.trim()) return toast.error("Party name is required");
    if (!form.party_state) return toast.error("State is required");
    if (!form.party_district) return toast.error("District is required");
    if (!form.party_billing_address.trim()) return toast.error("Billing address is required");
    if (!form.party_phone.trim()) return toast.error("Party phone is required");
    if (validItems.length === 0) return toast.error("Add at least one product");

    setLoading(true);
    try {
      const { data } = await api.post("/b2b/submit-ibo", {
        ibo_id: user.id,
        ibo_name: user.name,
        party_name: form.party_name.trim(),
        party_gst: form.party_gst.trim(),
        party_billing_address: form.party_billing_address.trim(),
        party_shipping_address: (form.party_shipping_address.trim() || form.party_billing_address.trim()),
        party_phone: form.party_phone.trim(),
        party_state: form.party_state,
        party_district: form.party_district,
        party_city: form.party_city.trim(),
        notes: form.notes.trim(),
        items: validItems,
      });
      toast.success(
        !data.auto_approved
          ? `Bill submitted · Pending admin approval · Ref: ${data.reference_number}`
          : data.party_created
            ? `Bill auto-approved · Party '${form.party_name.trim()}' created`
            : `Bill auto-approved · Ref: ${data.reference_number}`
      );
      resetForm();
      setOpenCreate(false);
      loadRows();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const totalAmt = rows.reduce((sum, r) => sum + Number(r.invoice_amount || 0), 0);

  // Client-side search across party name / reference / GST / phone / city.
  // Kept client-side because rows is already scoped to the caller's own
  // team by /b2b/my-submissions and typically small (<2k).
  const q = search.trim().toLowerCase();
  const visibleRows = q
    ? rows.filter((r) =>
        (r.party_name || "").toLowerCase().includes(q)
        || (r.reference_number || "").toLowerCase().includes(q)
        || (r.party_gst || "").toLowerCase().includes(q)
        || (r.party_phone || "").toLowerCase().includes(q)
        || (r.party_city || "").toLowerCase().includes(q)
      )
    : rows;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">
            {isAdmin ? "Create B2B Bill" : "B2B Bills"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? "Create a B2B bill directly. Bills you create here are auto-approved."
              : "Create bills for your parties — auto-approved as an authenticated IBO."}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" asChild data-testid="admin-view-all-bills">
              <a href="/b2b">
                <ExternalLink className="w-4 h-4 mr-1" /> All Submissions
              </a>
            </Button>
          )}
          <Button onClick={() => setOpenCreate(true)} data-testid="ibo-new-bill-btn">
            <Plus className="w-4 h-4 mr-1" /> {isAdmin ? "New Bill" : "Create Bill"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4" data-testid="ibo-stat-total">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Bills</div>
          <div className="text-2xl font-semibold mt-1">{rows.length}</div>
        </Card>
        <Card className="p-4" data-testid="ibo-stat-invoiced">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">With Invoice</div>
          <div className="text-2xl font-semibold mt-1">{rows.filter((r) => r.invoice_available).length}</div>
        </Card>
        <Card className="p-4" data-testid="ibo-stat-amount">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Invoice Value</div>
          <div className="text-2xl font-semibold mt-1">₹{totalAmt.toLocaleString("en-IN")}</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/20 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search party name, reference, GST, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="ibo-bills-search"
            />
          </div>
          {q && (
            <span className="text-xs text-muted-foreground" data-testid="ibo-bills-search-count">
              {visibleRows.length} of {rows.length} match{visibleRows.length === 1 ? "" : "es"}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Chat</TableHead>
                <TableHead>Track</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-10" data-testid="ibo-bills-empty">
                    {rows.length === 0
                      ? 'No B2B bills yet. Click "Create Bill" to get started.'
                      : "No bills match your search."}
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((r) => (
                <TableRow key={r.id} data-testid={`ibo-bill-row-${r.reference_number}`}>
                  <TableCell className="font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                      <span>{r.reference_number}</span>
                      {r.unread_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700 text-[10px] font-semibold"
                          title={`${r.unread_count} unread message${r.unread_count === 1 ? "" : "s"}`}
                          data-testid={`ibo-bill-unread-${r.reference_number}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                          {r.unread_count}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{r.party_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {[r.party_city, r.party_district, r.party_state].filter(Boolean).join(", ") || "—"}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 text-[10px] rounded ${STATUS_COLOR[r.status] || "bg-gray-100 text-gray-700"}`}>
                      {r.status}
                    </span>
                    {r.submission_source === "ibo" && (
                      <span className="ml-1 text-[10px] text-primary inline-flex items-center gap-0.5">
                        <Sparkles className="w-3 h-3" /> auto
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{r.invoice_available ? "Uploaded" : "Pending"}</TableCell>
                  <TableCell className="text-xs">{r.invoice_amount ? `₹${Number(r.invoice_amount).toLocaleString("en-IN")}` : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setChatOpen(r)}
                      className="h-7 px-2"
                      data-testid={`ibo-bill-chat-${r.reference_number}`}
                      title="Open conversation"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </TableCell>
                  <TableCell>
                    <a
                      href={`/b2b/track?ref=${encodeURIComponent(r.reference_number)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary text-xs inline-flex items-center gap-0.5 hover:underline"
                      data-testid={`ibo-bill-track-${r.reference_number}`}
                    >
                      Track <ExternalLink className="w-3 h-3" />
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> Create B2B Bill
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Party Name <span className="text-red-500">*</span></Label>
                <Input value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} data-testid="ibo-party-name" />
              </div>
              <div className="space-y-1">
                <Label>Party GST</Label>
                <Input value={form.party_gst} onChange={(e) => setForm({ ...form, party_gst: e.target.value })} data-testid="ibo-party-gst" />
              </div>
              <div className="space-y-1">
                <Label>Party Phone <span className="text-red-500">*</span></Label>
                <Input value={form.party_phone} onChange={(e) => setForm({ ...form, party_phone: e.target.value })} data-testid="ibo-party-phone" />
              </div>
              <div className="space-y-1">
                <Label>City</Label>
                <Input value={form.party_city} onChange={(e) => setForm({ ...form, party_city: e.target.value })} data-testid="ibo-party-city" />
              </div>
              <div className="md:col-span-2">
                <StateDistrictSelect
                  state={form.party_state}
                  district={form.party_district}
                  onStateChange={(v) => setForm({ ...form, party_state: v })}
                  onDistrictChange={(v) => setForm({ ...form, party_district: v })}
                  testidPrefix="ibo-party"
                  required
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Billing Address <span className="text-red-500">*</span></Label>
                <Textarea rows={2} value={form.party_billing_address} onChange={(e) => setForm({ ...form, party_billing_address: e.target.value })} data-testid="ibo-billing" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Shipping Address <span className="text-xs text-muted-foreground">(leave blank to reuse billing)</span></Label>
                <Textarea rows={2} value={form.party_shipping_address} onChange={(e) => setForm({ ...form, party_shipping_address: e.target.value })} data-testid="ibo-shipping" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ibo-notes" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Products <span className="text-red-500">*</span></Label>
                <Button variant="ghost" size="sm" onClick={addItem} data-testid="ibo-add-item"><Plus className="w-4 h-4 mr-1" /> Add row</Button>
              </div>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-8">
                      <SkuCombobox
                        skus={skus}
                        value={it.sku_id}
                        onChange={(v) => setItem(idx, "sku_id", v)}
                        testid={`ibo-item-sku-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        min={1}
                        value={it.quantity}
                        onChange={(e) => setItem(idx, "quantity", e.target.value)}
                        data-testid={`ibo-item-qty-${idx}`}
                        placeholder="Qty"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} disabled={items.length === 1}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-md p-3 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                As an authenticated IBO, this bill is <b>auto-approved</b>. The accountant will directly upload the tax invoice next.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button onClick={submit} disabled={loading} data-testid="ibo-submit-bill">
              {loading ? "Submitting…" : "Submit Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat dialog — IBO can message the accountant/admin about this bill */}
      <Dialog open={!!chatOpen} onOpenChange={(o) => { if (!o) { setChatOpen(null); load(); } }}>
        <DialogContent className="max-w-lg" data-testid="ibo-bill-chat-dialog">
          {chatOpen && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Chat · {chatOpen.reference_number}
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground mb-2">
                Party: <b>{chatOpen.party_name}</b> · Status: {chatOpen.status}
              </div>
              <B2BChat submissionId={chatOpen.id} currentUserId={user.id} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
